import { Component } from '@theme/component';
import { Slideshow } from '@theme/slideshow';
import { DialogComponent } from './dialog.js';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

const REDIRECT_RESPONSE = 'minimal';

const CATEGORY_QUERY = 'results';

const PAGE_QUERY = 'p'; // Do no conflict with native 'page'

const CAROUSEL_CARDS_COUNT = 10;

const PAGINATION_MAX_PAGES = 5;

const PAGINATION_MIDDLE_OFFSET = Math.floor(PAGINATION_MAX_PAGES / 2.0);

/**
 * A custom element that displays search results from search spring.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} grid
 * @property {HTMLTemplateElement} cardTemplate
 * @property {HTMLTemplateElement} listFilterTemplate
 * @property {HTMLTemplateElement} checkboxTemplate
 * @property {HTMLTemplateElement} filterRemoveButtonsTemplate
 * @property {HTMLTemplateElement} filterRemoveButtonsClearAllTemplate
 * @property {HTMLTemplateElement} pagePaginationItemTemplate
 * @property {HTMLTemplateElement} currentPaginationItemTemplate
 *
 * @extends {Component<Refs>}
 */
class SearchSpringSearchResults extends Component {
  #initialized = false;

  #searchCategoryMapping = {
    all: { id: "all", siteId: "", field: "", label: "All", value: "", showPrice: true, page: "pageAll", lastCall: {request: null, response: null} },
    products: { id: "products", siteId: "", field: "bgfilter.visibility", label: "Products", value: "Search", showPrice: true, page: "pageGrid", lastCall: {request: null, response: null} },
    projects: { id: "projects", siteId: "", field: "bgfilter.ss_type", label: "Projects", value: "project", showPrice: false, page: "pageGrid", lastCall: {request: null, response: null} },
    tutorials: { id: "tutorials", siteId: "", field: "bgfilter.ss_type", label: "Tutorials", value: "tutorial", showPrice: false, page: "pageGrid", lastCall: {request: null, response: null} },
  };
  #searchCategories = Object.values(this.#searchCategoryMapping);
  #inidiviualSearchCategories = this.#searchCategories.filter(({id}) => id != this.#searchCategoryMapping.all.id);
  #defaultCategory = this.#searchCategoryMapping.all;
  #pageNames = this.#searchCategories.map(({page}) => page);

  connectedCallback() {
    super.connectedCallback();

    this.#searchCategories.forEach((category) => {
      const attr = `data-site-id-${category.id}`;
      if (this.hasAttribute(attr)) {
        const siteId = this.getAttribute(attr);
        if (siteId) {
          category.siteId = siteId;
        }
      }
    })

    if (!this.#initialized) {
      this.#initialized = true;
      this.#search();
      this.#inidiviualSearchCategories.forEach((category) => this.#initializeSearchResultsCarousel(category.id));
    }

    // Enable the back button after we push history state
    window.addEventListener('popstate', (event) => {
      if (event?.state?.params) {
        const params = new URLSearchParams(event.state.params);
        this.#newSearchWithParams(params);
      }
    })
  }

  /**
   * Determine the current category based on the url
   */
  get category() {
    if (!this.currentCategory) {
      const params = new URLSearchParams(window.location.search);
      const resultsParam = params.get(CATEGORY_QUERY);
      const category = this.#searchCategories.find((category) => category.id == resultsParam);
      this.currentCategory = category ? category : this.#defaultCategory;
    }
    return this.currentCategory;
  }

  /**
   * We're split between the carousel page and the grid page
   */
  get categoryPages() {
    /**
     * @type {HTMLElement[]}
     */
    const foundPages = [];
    this.#pageNames.forEach((name) => {
      const page = this.refs[name];
      if (page && page instanceof HTMLElement) foundPages.push(page);
    });
    return foundPages;
  }

  /**
   * Allow re-reading the current category based on url. Used for searching without a page reload
   */
  #resetCategory() {
    this.currentCategory = null;
  }

  /**
   * Build a search url
   *
   * @param {URLSearchParams} params
   */
  #newSearchUrlWithParams(params) {
    return window.location.origin + window.location.pathname + `?${params.toString()}`;
  }

  /**
   * Starts a new search without a page reload
   *
   * @param {URLSearchParams} params
   */
  #newSearchWithParams(params) {
    const url = this.#newSearchUrlWithParams(params);
    history.pushState({params: params.toString()}, '', url);
    this.#resetCategory();
    this.#search(params);
  }

  /**
   * Run the search and update the display
   */
  async #search(params = new URLSearchParams(window.location.search)) {
    const { category } = this;
    const filters = Array.from(params.entries()).filter(([key]) => key.startsWith('filter.'));
    const page = parseInt(params.get(PAGE_QUERY) ?? '1');

    // todo: use a loader instead of hiding the content
    this.categoryPages.forEach((page) => page.style.display = 'none');
    const categoryPage = this.refs[category.page];
    if (categoryPage && categoryPage instanceof HTMLElement) {
      categoryPage.style.opacity = '0';
      categoryPage.style.visibility = 'hidden';
      categoryPage.style.removeProperty('display');
    }

    // SS will return redirect URLs for certain configured search terms
    const checkRedirect = (res) => {
      if (res?.merchandising?.redirect) {
        window.location.href = res?.merchandising?.redirect;
      }
    }

    try {
      if (category == this.#searchCategoryMapping.all) {
        const fetchAndRender = async (category) => {
          const response = await this.#searchCategory(category, filters, page);
          checkRedirect(response);
          this.#renderPageTitle('pageAllTitleTemplate', 'pageAllTitle', response.pagination);
          this.#renderCarouselTitle(category, response.pagination);
          this.#renderPageAll(response, category);
        }
        await Promise.all(this.#inidiviualSearchCategories.map((category) => fetchAndRender(category)));
      } else {
        const response = await this.#searchCategory(category, filters, page);
        checkRedirect(response);
        this.#renderPageTitle('productGridTitleTemplate', 'productGridTitle', response.pagination);
        this.#renderPageTitle('productGridSubTitleTemplate', 'productGridSubTitle', response.pagination);
        this.#renderPageGrid(response);
      }
    } catch(err) {
      // todo: error handling
      console.error(err);
    }
    finally {
      // content is ready to show
      if (categoryPage && categoryPage instanceof HTMLElement) {
        categoryPage.style.opacity = '1';
        categoryPage.style.removeProperty('visibility');
      }
    }
  }

  /**
   * Perform the search for a specified category, checking the 'cache' before making
   * network calls
   *
   * @param {any} category
   * @param {[any, any][]} filters
   * @param {number} page
   */
  async #searchCategory(category, filters, page=1) {
    const {siteId,lastCall} = category;
    const params = this.#buildSearchParams(category, filters, page);
    if (params.toString() == lastCall.request) {
      return lastCall.response;
    }
    const res = await this.#fetchSearchCategory(siteId, params);
    lastCall.request = params.toString();
    lastCall.response = res;
    return res;
  }

  /**
   * Build the fetch input
   * @param {any} category
   * @param {[any, any][]} filters
   * @param {number} page
   */
  #buildSearchParams(category, filters, page) {
    const siteId = category.siteId;
    const query = this.#getSearchQuery();

    const searchParams = new URLSearchParams();
    searchParams.append('siteId', siteId);
    searchParams.append('resultsFormat', 'json');
    searchParams.append('q', query);
    searchParams.append('redirectResponse', REDIRECT_RESPONSE);
    searchParams.append(category.field, category.value);
    if (page > 1) searchParams.append('page', `${page}`);
    filters.forEach(([key, value]) => searchParams.append(key, value));

    return searchParams;
  }

  /**
   * Make the SS fetch api call
   * @param {string} siteId
   * @param {URLSearchParams} searchParams
   */
  async #fetchSearchCategory(siteId, searchParams) {
    const res = await fetch(`https://${siteId}.a.searchspring.io/api/search/search.json?${searchParams.toString()}`, {
      method: 'GET',
      headers: {accept: 'application/json'},
      redirect: 'follow',
    });
    if (!res.ok) {
      // todo: error handling
      throw new Error('Search request failed.');
    }
    if (res.redirected) {
      window.location.href = res.url;
      return;
    }
    return res.json();
  }

  /**
   * Renders the carousel page
   *
   * @param {{ results: any[]; }} response
   * @param {{ id: string; }} category
   */
  #renderPageAll(response, category) {
    const wrappers = this.#refs('searchProductList');
    if (!(wrappers instanceof Array)) return;
    const wrapper = wrappers.find((element) =>
      element instanceof HTMLElement &&
      element.dataset.bind === `${category.id}-carousel:wrapper`
    )
    if (!(wrapper instanceof HTMLElement)) return;

    if (!response?.results?.length) {
      wrapper.style.display = 'none';
      return;
    }

    this.#renderSearchResultsCarousel(response.results.slice(0, CAROUSEL_CARDS_COUNT), category.id);
    wrapper.style.removeProperty('display');
  }

  /**
   * Renders the grid page
   *
   * @param {{ results: any[]; facets: any[]; filterSummary: any[]; pagination: any; }} response
   */
  #renderPageGrid(response) {
    this.#renderSearchResultsGrid(response?.results);
    this.#renderCategoryFilters();
    this.#renderSearchFilters(response?.facets);
    this.#renderFilterRemoveButtons(response?.filterSummary);
    this.#renderPagination(
      response?.pagination,
      this.#activeFilterValues(response?.filterSummary),
    );
  }

  /**
   * Render the page title using the provided template refs
   * @param {string} templateRef
   * @param {string} containerRef
   * @param {any} pagination
   */
  #renderPageTitle(templateRef, containerRef, pagination) {
    const template = this.#ref(templateRef);
    if (!(template instanceof HTMLTemplateElement)) return;

    const container = this.#ref(containerRef);
    if (!(container instanceof Element)) return;

    const fragment = document.createDocumentFragment();

    const node = template.content.firstElementChild?.cloneNode(true);
    if (!(node instanceof Element)) return;

    this.#renderSearchTitle(node, pagination);

    fragment.append(node);
    container.replaceChildren(fragment);
  }

  /**
   * Find and populate the carousel title
   *
   * @param {any} category
   * @param {any} pagination
   * @returns
   */
  #renderCarouselTitle(category, pagination) {
    const template = this.#ref('pageAllSubTitleTemplate');
    if (!(template instanceof HTMLTemplateElement)) return;

    const binding = `${category.id}-subtitle:text`;
    const container = this.querySelector(`[data-bind="${binding}"]`);
    if (!container) return;

    const node = template.content.firstElementChild?.cloneNode(true);
    if (!(node instanceof Element)) return;

    this.#renderSearchTitle(node, pagination, category);
    container.replaceChildren(node);
  }

  /**
   * Render the search title with the current query and category
   * Expects the pattern [[replacement_key] text to show if replacement key exists]
   * @param {Element} container - the search title node
   * @param {{ end: any; totalResults: any; }} pagination
   * @param {any} category
   */
  #renderSearchTitle(container, pagination, category = this.category) {
    const regexEscape = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const query = this.#getSearchQuery();
    const searchCountRange = pagination.end === pagination.totalResults
      ? `${pagination.end}`
      : `${pagination.end} of ${pagination.totalResults}`;
    const searchCountTotal = `${pagination.totalResults}`;
    const replacementKeys = {
      '[query]': query,
      '[search_category]': category.label,
      '[search_count_range]': searchCountRange,
      '[search_count_total]': searchCountTotal,
    }
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null,
    );
    let title;
    while ((title = walker.nextNode())) {
      for (const [key, replacement] of Object.entries(replacementKeys)) {
        if (title.nodeValue?.includes(key)) {
          const regex = new RegExp(`\\[(.*?)${regexEscape(key)}(.*?)\\]`);
          const substitute = replacement ? `$1${replacement}$2` : '';
          title.nodeValue = title.nodeValue?.replace(regex, substitute) ?? null;
        }
      }
    }
  }

  /**
   * Render the search results as product cards in a carousel
   *
   * @param {any[]} results
   * @param {string} categoryId
   */
  #renderSearchResultsCarousel(results, categoryId) {
    const templates = this.#refs('listItemTemplate');
    if (!(templates instanceof Array)) return;
    const template = templates.find((element) =>
      element instanceof HTMLElement &&
      element.dataset.bind === `${categoryId}-carousel:template`)
    if (!(template instanceof HTMLTemplateElement)) return;

    const container = this.querySelector(`[data-bind="${categoryId}-carousel:container"]`);
    if (!(container instanceof Element)) return;

    let slide = 0;
    this.#renderListFromTemplate(results, template, container, (node, data) => {
      this.#hydrateProductCard(node, data);
      if (node instanceof HTMLElement) node.style.setProperty('view-timeline-name', `--slide-${slide}`);
      slide++;
    });

    const slideshow = container.closest('[ref="resourceListCarousel"]');
    if (slideshow instanceof Slideshow) {
      slideshow.initializeExpectingSlideControl();
    }

  }

  /**
   * Initializes static content in the carousel
   *
   * @param {string} categoryId
   */
  #initializeSearchResultsCarousel(categoryId) {
    const viewAllButton = this.querySelector(`[data-bind="${categoryId}-view-all:button"]`);
    if (viewAllButton) {
      viewAllButton.addEventListener('click', () => {
        const newParameters = this.#baseParameters(categoryId);
        this.#newSearchWithParams(newParameters);
      })
    }
  }

  /**
   * Render the search results as product cards in a product grid
   *
   * @param {any[]} results
   */
  #renderSearchResultsGrid(results) {
    const template = this.#ref('cardTemplate');
    if (!(template instanceof HTMLTemplateElement)) return;

    const container = this.#ref('grid');
    if (!(container instanceof Element)) return;

    this.#renderListFromTemplate(results, template, container, (node, data) => this.#hydrateProductCard(node, data));
  }

  /**
   * Renders the top level category filters. These are tied to bgfilter.
   */
  #renderCategoryFilters() {
    const categoryFilters = this.#inidiviualSearchCategories.map((value) => ({...value, active: value.id == this.category.id }));

    const template = this.#ref('listFilterTemplate');
    if (!(template instanceof HTMLTemplateElement)) return;

    const buttonTemplates = this.#refs('buttonTemplate');
    const viewAllTemplate = buttonTemplates.find((button) =>
      button instanceof HTMLElement && button.dataset.bind === `all-view-all:template`
    );

    const containers = this.querySelectorAll('.facets__top-filters-wrapper')
    containers.forEach((container) => {
      // category filters
      this.#renderListFromTemplate([{values: categoryFilters}], template, container, (node, data) => {
        if (!(node instanceof Element)) return;
        const label = node.querySelector('.facets__label');

        if (label) {
          label.textContent = data.label ?? null;
          if (data.values) this.#renderFilterCheckboxes(node, data.values);
        }
      });

      // view all
      if (viewAllTemplate instanceof HTMLTemplateElement) {
        this.#renderListFromTemplate([viewAllTemplate], viewAllTemplate, container, (node, _data) => {
          if (!(node instanceof HTMLElement)) return;
          node?.classList.add('facets__view-all-button');
          node?.setAttribute('on:click', '#filters-drawer/closeDialog');
          const categoryId = this.#searchCategoryMapping.all.id;
          if (node.dataset.bind == `${categoryId}-view-all:button`) {
            node.addEventListener('click', () => {
              const newParameters = this.#baseParameters(categoryId);
              this.#newSearchWithParams(newParameters);
            });
          }

        }, false);
      }
    });
  }

  /**
   * Translate summary into filter query params
   *
   * @param {any[]} filterSummary
   * @returns array mapping of the active filters
   */
  #activeFilterValues(filterSummary) {
    return filterSummary.flatMap((data) => {
      const attributes = this.#buildFilterAttributes(data.field, data.filterValue, data.value.rangeLow, data.value.rangeHigh)
      const fields = attributes.field.split(',');
      const values = attributes.value?.split(',');
      const filters = [];
      for (let i = 0; i< fields.length; ++i) {
        if (fields[i] && values[i]) {
          filters.push([fields[i], values[i]]);
        }
      }
      return filters;
    });
  }

  /**
   * Consistent way to build the input attributes for facets and facetSummary
   * @param {string} field
   * @param {string} [value]
   * @param {string} [low]
   * @param {string} [high]
   */
  #buildFilterAttributes(field, value, low, high) {
    if (low && high) {
        const fieldAtt = `filter.${field}.low,filter.${field}.high`;
        const valueAtt = `${low},${high}`;
      return {
        field: fieldAtt,
        value: valueAtt,
        id: `${fieldAtt}:${valueAtt}`,
      }
    }
    const fieldAtt = `filter.${field}`;
    return {field: fieldAtt, value, id: `${fieldAtt}:${value}`};
  }

  /**
   * Render the search results filters
   *
   * @param {any[]} facets
   */
  #renderSearchFilters(facets) {
    facets.forEach((facet) => {
      facet.values = facet.values.map((facetValue) => ({
        ...facetValue,
        ...this.#buildFilterAttributes(facet.field, facetValue.value, facetValue.low, facetValue.high),
      }))
    });

    const template = this.#ref('listFilterTemplate');
    if (!(template instanceof HTMLTemplateElement)) return;

    const containers = this.querySelectorAll('.facets__filters-wrapper');
    containers.forEach((container) => {
      if (!(container instanceof Element)) return;

      this.#renderListFromTemplate(facets, template, container, (node, data) => {
        if (!(node instanceof Element)) return;
        const label = node.querySelector('.facets__label');

        if (label) {
          label.textContent = data.label ?? null;
          if (data.values) this.#renderFilterCheckboxes(node, data.values);
        }
      });
    });
  }

  /**
   * Render the search results filters
   *
   * @param {Element} parent
   * @param {any[]} values
   */
  #renderFilterCheckboxes(parent, values) {
    const template = this.#ref('checkboxTemplate');
    if (!(template instanceof HTMLTemplateElement)) return;

    const container = parent.querySelector('ul.facets__inputs-list');
    if (!(container instanceof Element)) return;

    this.#renderListFromTemplate(values, template, container, (node, data) => {
      if (!(node instanceof Element)) return;
      const input = node.querySelector('input.checkbox__input');
      const label = node.querySelector('span.checkbox__label-text');

      if (input instanceof HTMLInputElement) {
        input.setAttribute('name', data.field);
        input.setAttribute('value', data.value);
        input.setAttribute('id', data.id);
        input.setAttribute('data-label', data.label);
        input.checked = data.active;
      }
      if (label) label.textContent = data.label ?? null;

      node.addEventListener('click', (event) => {
        this.#updateFilters(input);
      });
    });
  }

  /**
   * Render the remove and clear all buttons
   *
   * @param {any[]} filterSummary
   */
  #renderFilterRemoveButtons(filterSummary) {
    const removeButtonTemplate = this.#ref('filterRemoveButtonsTemplate');
    const clearAllTemplate = this.#ref('filterRemoveButtonsClearAllTemplate');

    const containers = this.querySelectorAll('.facets-remove');
    containers.forEach((container) => {
      if (!(container instanceof Element)) return;
      this.#renderListFromTemplate(filterSummary, removeButtonTemplate, container, (node, data) => {
        if (!(node instanceof Element)) return;
        const label = node.querySelector('span.facets-remove__label');
        if (label) label.textContent = data.filterValue;
        const {id} = this.#buildFilterAttributes(data.field, data.filterValue, data.value.rangeLow, data.value.rangeHigh);
        if (id) node.setAttribute('data-filterid', id);
        node.addEventListener('click', () => this.#clearSingleFilter(node));
      });
      if (filterSummary?.length) {
        this.#renderListFromTemplate([1], clearAllTemplate, container, (node, data) => {
          if (!(node instanceof Element)) return;
          const button = node.querySelector('button');
          if (button) button.addEventListener('click', () => this.#clearAllFilters());
        }, false);
      }
    })
  }

  /**
   * Render pagination controls
   * @param {{totalPages: number;currentPage: number;}} pagination
   * @param {[string, string]} filters
   */
  #renderPagination(pagination, filters) {
    const pageTemplate = this.#ref('pagePaginationItemTemplate');
    const currentTemplate = this.#ref('currentPaginationItemTemplate');
    if (!(pageTemplate instanceof HTMLTemplateElement) || !(currentTemplate instanceof HTMLTemplateElement)) return;

    // Grab the slice of pages to show, centered around the active page
    let firstPage = Math.max(pagination.currentPage - PAGINATION_MIDDLE_OFFSET, 1);
    const lastPage = Math.min(firstPage + (PAGINATION_MAX_PAGES - 1), pagination.totalPages);
    if (lastPage == pagination.totalPages) {
      firstPage = Math.max(lastPage - (PAGINATION_MAX_PAGES - 1), 1);
    }

    const pages = [];
    for (let page = firstPage; page <= lastPage; ++page) {
      pages.push({
        page,
        template: page === pagination.currentPage ? currentTemplate : pageTemplate,
      });
    }

    const containers = this.querySelectorAll('.pagination__list');
    containers.forEach((container) => {
      this.#renderListFromTemplate(pages, pageTemplate, container, (node, data) => {
        if (!(node instanceof Element)) return;

        const title = node.querySelector('[data-bind]');
        if (!(title instanceof HTMLElement)) return;

        title.textContent = `${data.page}`;

        const property = title.dataset.bind?.split(':')?.pop();
        if (property === 'url' && title instanceof HTMLAnchorElement) {
          const newParameters = this.#baseParameters(this.category.id, filters);
          newParameters.append(PAGE_QUERY, `${data.page}`);
          title.href = this.#newSearchUrlWithParams(newParameters);
          title.setAttribute('aria-label', `Page ${data.page}`);
        }
      })
    });
  }

  /**
   * Handle a remove button click
   * @param {HTMLElement} removeElement
   */
  #clearSingleFilter(removeElement) {
    removeElement.style.display = 'none';
    const filterId = removeElement.dataset.filterid;
    const filterElement = this.querySelector(`[id="${filterId}"]`);
    this.#updateFilters(filterElement);
  }

  /**
   * Handle a clear all button click
   */
  #clearAllFilters() {
    const newParameters = this.#baseParameters();
    this.#newSearchWithParams(newParameters);
  }

  /**
   * Handle a filter click
   *
   * @param {Element | null} [input]
   */
  #updateFilters(input) {
    if (!(input instanceof HTMLInputElement)) return;

    if (input.name?.startsWith('bgfilter.')) {
      // top level filter
      const newParameters = this.#baseParameters(input.id);
      this.#newSearchWithParams(newParameters);
    } else {
      // sub filter
      input.checked = !input.checked;

      const facetsForm = input.closest('[ref="facetsForm"]');
      if (!(facetsForm instanceof HTMLFormElement)) return;

      const formData = new FormData(facetsForm);

      const newParameters = this.#baseParameters();
      for (const [key, value] of formData.entries()) {
        const fields = key.split(',');
        const values = value.toString().split(',');
        for (let i = 0; i<fields.length; i++) {
          if (fields[i] && values[i]) {
            newParameters.append(fields[i], values[i]);
          }
        }
      }
      this.#newSearchWithParams(newParameters);
    }
  }

  /**
   * Always include the current search term and top level category
   *
   * @param {string} categoryId
   * @param {([string, string]) | null} filters
   * @returns URLSearchParams
   */
  #baseParameters(categoryId = this.category.id, filters = null) {
    const parameters = new URLSearchParams();
    const searchQuery = this.#getSearchQuery();
    parameters.set(SEARCH_QUERY, searchQuery);
    parameters.set(CATEGORY_QUERY, categoryId);
    if (filters) {
      for (const [key, value] of filters) {
        parameters.append(key, value.toString());
      }
    }
    return parameters;
  }

  /**
   * Render the search results as product cards
   * @param {any[]} results
   * @param {HTMLTemplateElement} template
   * @param {Element} container
   * @param {(arg0: Node, arg1: { [x: string]: any; }) => void} hydrateFn
   */
  #renderListFromTemplate(results, template, container, hydrateFn, replace = true) {
    const fragment = document.createDocumentFragment();

    for (let res of results) {
      if (!res) {
        continue;
      }
      // allow a template override if one is provided in the result list
      const templateToUse = res.template ? res.template : template;
      const node = templateToUse.content.firstElementChild?.cloneNode(true);
      if (!node) continue;

      hydrateFn(node, res);

      fragment.append(node);
    }

    if (replace) {
      container.replaceChildren(fragment);
    } else {
      container.appendChild(fragment);
    }
  }

  /**
   * Populate the bindings with data from the search results
   *
   * @param { Node } node
   * @param {{ [x: string]: string; }} data
   */
  #hydrateProductCard(node, data) {
    if (!(node instanceof Element)) return;
    const bindings = node.querySelectorAll('[data-bind]');

    // Map additional fields that may be missing
    data = this.#extendMoneyFields(data);

    bindings.forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      if (!element.dataset.bind) return;

      const [fields, property] = element.dataset.bind.split(':');
      if (!property) return;

      // Find the first matching field in our data. We're using an array of field bindings because
      // the title fields are not the same across our data sources.
      let field = fields;
      const fieldArry = fields?.split(',') ?? [];
      for (let fieldName of fieldArry) {
        if (fieldName && data[fieldName]) {
          field = fieldName;
          break;
        }
      };

      let value = (field && data[field]) ? data[field] : null;
      value = value ? value : null;

      // The text returned by SS is encoded. For example, & is represented as &amp;
      const htmlDecode = (input) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(input, "text/html");
        return doc.documentElement.textContent;
      };

      switch (property) {
        case 'text': {
          if (field == 'price-discount-percent') {
            value = this.#saleProduct(data) ? this.#createDiscountPercent(data) : null;
          }
          if (field == 'mfield_sale_discount_percentage') {
            value = this.#saleProduct(data) ? this.#parseDiscountPercentFromRange(data[field]) : null;
          }
          if (field == 'category_description') {
            element.style.whiteSpace = 'pre-wrap'; // honor new lines
          }
          const elementToModify = element.children.item(0) ? element.children.item(0) : element;
          if (elementToModify) {
            elementToModify.textContent = value ? htmlDecode(value) : null;
          }
          break;
        }
        case 'text|money':
          // SS forces us to populate a price field. However not all of our data has a price!
          if (!this.category?.showPrice) {
            value = null;
          }

          const moneyValue = value ? parseFloat(value) : null;
          let moneyStr = moneyValue ? `$${moneyValue.toFixed(2)}` : null;
          if (field == 'msrp' && moneyValue) {
            moneyStr = this.#saleProduct(data)
              ? this.#createPriceRange(moneyValue, data.ss_min_retail, data.ss_max_retail)
              : null;
          } else if (field == 'price' && moneyValue) {
            if (this.#saleProduct(data)) { // sale
              moneyStr = this.#createPriceRange(moneyValue, data.ss_min_price, data.ss_max_price)
            }
          }
          element.textContent = moneyStr;
          break;
        case 'img': {
          const elementToModify = (element instanceof HTMLImageElement)
            ? element
            : element.children.item(0);
          if (elementToModify instanceof HTMLImageElement) {
            if (value) {
              elementToModify.src = value;
            } else {
              elementToModify.removeAttribute('src');
            }
            elementToModify.removeAttribute('srcset');
          }
          break;
        }
        case 'href': {
          if (field == 'url') {
            if (element.hasAttribute('aria-label') && data.display_name) {
              element.setAttribute('aria-label', data.display_name);
            } else {
              element.removeAttribute('aria-label');
            }
          }
          element[property] = value;
          break;
        }
        case 'badge':
        case 'quick-add':
          element.remove(); // todo!
          break;
        default:
          element[property] = value;
          break;
      }
    });
  }

  /**
   * Special case to transform data into a discount string
   *
   * @param {{ [x: string]: string; }} product
   */
  #createDiscountPercent(product) {
    const minMsrp = product.ss_min_retail ? parseFloat(product.ss_min_retail) : null;
    const maxMsrp = product.msrp ? parseFloat(product.msrp) : null;
    const maxPrice = product.ss_max_price ? parseFloat(product.ss_max_price) : null;
    const minPrice = product.ss_min_price ? parseFloat(product.ss_min_price) : null;
    if (minMsrp && maxMsrp && maxPrice && minPrice) {
        const maxDiscount = Math.round((maxMsrp - maxPrice) / maxMsrp * 100.0);
        const minDiscount = Math.round((minMsrp - minPrice) / minMsrp * 100.0);
      if (maxDiscount != minDiscount) {
        return `Up to ${maxDiscount}% off`
      } else {
        return `${maxDiscount}% off`
      }
    }
    return null;
  }
 /**
  * Parse an array of %off strings to form a single percent off label
  * @param {string[]} percentages
  */
  #parseDiscountPercentFromRange(percentages) {
    if (!percentages || !Array.isArray(percentages)) {
      return null;
    }
    if (percentages?.length == 1) {
      return percentages[0];
    } else if (percentages?.length > 1) {
      const percentageValues = percentages.map((percentage) => parseInt(percentage.replace(/[^\d]/g, ''), 10))
        .filter((val) => !Number.isNaN(val));
      const maxPercentage = Math.max(...percentageValues);
      return `Up to ${maxPercentage}% off`;
    }
    return null;
  }

  /**
   * Special case to transform a price into a price range string
   *
   * @param {number} moneyValue
   * @param {string | undefined} minPriceStr
   * @param {string | undefined} maxPriceStr
   */
  #createPriceRange(moneyValue, minPriceStr, maxPriceStr) {
    const maxPrice = maxPriceStr ? parseFloat(maxPriceStr) : moneyValue;
    const minPrice = minPriceStr ? parseFloat(minPriceStr) : moneyValue;
    if (maxPrice > minPrice) {
      return `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`
    }
    return `$${moneyValue.toFixed(2)}`;
  }

  /**
   * Whether or not the product is on sale
   *
   * @param {{ [x: string]: string; }} product
   */
  #saleProduct(product) {
    const msrp = product.msrp ? parseFloat(product.msrp) : null;
    if (!msrp) return false;
    const price = product.price ? parseFloat(product.price) : null;
    if (!price) return false;
    return msrp > price;
  }

  /**
   * Extend the product data if its missing money fields
   * @param {any} product
   * @returns
   */
  #extendMoneyFields(product) {
    const extendedProduct = product;
    if (!('msrp' in product) && ('mfield_custom_compare_at_price_max' in product)) {
      extendedProduct['msrp'] = product['mfield_custom_compare_at_price_max'];
    }
    if (!('ss_min_retail' in product) && ('mfield_custom_compare_at_price_min' in product)) {
      extendedProduct['ss_min_retail'] = product['mfield_custom_compare_at_price_min'];
    }
    if (!('ss_min_price' in product) && ('ss_price' in product)) {
      extendedProduct['ss_min_price'] = product['ss_price'];
    }
    if (!('ss_max_price' in product) && ('ss_price' in product)) {
      extendedProduct['ss_max_price'] = product['ss_price'];
    }
    if (('ss_price_range' in product && product['ss_price_range'].length == 2)) {
      extendedProduct['ss_min_price'] = product['ss_price_range'][0];
      extendedProduct['ss_max_price'] = product['ss_price_range'][1];
    }
    return extendedProduct;
  }

  /**
   * Gets the search query parameter from the current URL
   *
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  /**
   * @param {string} refName
   */
  #ref(refName) {
    return this.querySelector(`[ref="${refName}"]`);
  }

  /**
   * @param {string} refName
   */
  #refs(refName) {
    return Array.from(this.querySelectorAll(`[ref="${refName}[]"]`));
  }
}

if (!customElements.get('ss-search-results')) {
  customElements.define('ss-search-results', SearchSpringSearchResults);
}
